import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CButton } from '@coreui/react-pro';
import dayjs from 'dayjs';

import { isAuth } from '../../../../../../helpers/auth';
import {
     checkCeProductIsRPT,
     checkProductHasReservedSeat,
     checkProductHasSeat,
     checkProductTypeUnlimitedPurchase,
     getTimeZoneGuess,
     numberWithCommas,
     tryParseJSON,
} from '../../../../../../utils';
import {
     CART_TYPES,
     LOCAL_TIME_ZONE,
     PRODUCT_TYPES_REQUIRE_EVENT,
     STATUS,
     SUBSCRIBER_PATH,
     TAB_SELECT_PURCHASE,
     TRAINING_PRODUCT_TYPES,
} from '../../../../../../constants';

import TooltipWithIcon from '../../../../../General/Tooltips/TooltipWithIcon';
import SelectTrainingDate from './SelectTrainingDate';
import { TrainingProductCardContext } from './TrainingProductCard';

import { fetchSubscriberProductsRequest } from '../../../../../CMS/Subscriber/Products/actions';
import { fetchGuestContentManagements } from '../../../../../../containers/guest/action';
import { fetchCoursesRequest } from '../../../../Subscriber/MyLeaning/Course/actions';

const PersonalTrainingTab = () => {
     const { listProductCart, listProducts, product, setProductId, listProductTypeRequireEvent, disableCart, addToCart, disableCartButton } =
          useContext(TrainingProductCardContext);

     const dispatch = useDispatch();
     const navigate = useNavigate();
     const courses = useSelector((state) => state.subscriber.courses);
     const products = useSelector((state) => state.subscriber.products.list);
     const contentManagements = useSelector((state) => state.subscriber.contentManagements);
     const listActiveSeatByUser = useSelector((state) => state.subscriber.listActiveSeatByUser);
     const awaitingOrderItems = useSelector((state) => state.subscriber.awaitingOrderItems);

     const navigateSellingGroup = () => {
          if (!listProducts.length) {
               navigate(SUBSCRIBER_PATH.SELLING_GROUPS);
          }
     };

     const fetchDataContentManagements = () => {
          if (!contentManagements.length) {
               dispatch(fetchGuestContentManagements());
          }
     };

     const fetchProducts = () => {
          if (!products?.length) {
               dispatch(fetchSubscriberProductsRequest());
          }
     };

     const fetchCourses = () => {
          if (!courses && isAuth()) {
               dispatch(fetchCoursesRequest());
          }
     };

     useEffect(() => {
          navigateSellingGroup();
          fetchDataContentManagements();
          fetchProducts();
          fetchCourses();
     }, []); // eslint-disable-line react-hooks/exhaustive-deps

     const listEventNoSelectInCart = listProductCart.filter((itemCart) => {
          const isLiveTraining = itemCart.productType?.trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;

          if (!isLiveTraining) return false;

          if (itemCart.selectedEventCredit) return itemCart.selectedEventCreditQuantity < 1;

          const isNotSelectEvent = itemCart.listEventSelected?.length <= 0;

          return isNotSelectEvent;
     });

     const checkOnlyEventNotSelectInCart = listEventNoSelectInCart?.length === listProductCart?.length ? true : false;

     const checkEventHasSeatInCart = listProductCart
          .filter((itemCart) => itemCart.productType.trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING)
          .some((itemCart) => {
               if (itemCart.events && itemCart.listEventSelected?.length > 0) {
                    return itemCart.listEventSelected.some((itemEvent) => checkProductHasSeat(listActiveSeatByUser, itemCart.id, itemEvent.value));
               } else {
                    return false;
               }
          });

     const titleTabPersonal = contentManagements.find((content) => content.key === 'pageSellingGroupDetailTitleTabPersonal');
     const contentTooltipTabPersonal = contentManagements.find((content) => content.key === 'pageSellingGroupDetailContentTooltipTabPersonal');
     const messageCannotPurchase = contentManagements.find((content) => content.key === 'pageSellingGroupDetailMessageCannotPurchase');
     const contentTooltipCE = contentManagements.find((content) => content.key === 'pageSellingGroupDetailContentTooltipCE');

     const timezoneSelected = localStorage.getItem(LOCAL_TIME_ZONE) || getTimeZoneGuess();

     const today = dayjs().tz(timezoneSelected);

     const priceSingle = listProductCart.reduce((a, b) => {
          const isLiveTraining = b.productType.trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;

          const quantityEventProduct = b.listEventSelected?.reduce((i, j) => {
               let newQuantityEvent = 0;
               if (j.value) {
                    newQuantityEvent = j.quantityEvent;
               }
               return i + (newQuantityEvent || 0);
          }, 0);

          let priceProduct = !isLiveTraining || (isLiveTraining && b.listEventSelected?.length > 0) ? b['price'] : 0;

          if (isLiveTraining && b.selectedEventCredit && b.selectedEventCreditQuantity > 0) {
               priceProduct = b.selectedEventCreditQuantity * b['price'];

               return a + (priceProduct || 0);
          }

          if (quantityEventProduct > 0) {
               priceProduct = priceProduct * quantityEventProduct;
          } else {
               if (b.listEventSelected?.some((ev) => ev.value === undefined) && b.events.length > 0) {
                    priceProduct = 0;
               }
          }
          return a + (priceProduct || 0);
     }, 0);

     const eventHasTrainingCart = listProductCart.some((cart) => {
          const checkLiveTraining = cart.productType.trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
          if (checkLiveTraining && cart.listEventSelected?.length <= 0) {
               return true;
          }
          return false;
     });

     const isInvisiblePurchase = product?.purchaseType === CART_TYPES.INVISIBLE;

     const checkEventCreditSelected = listProductCart.find((item) => {
          if (item.listEventSelected?.length) return true;

          return item.allowPurchaseEventCredit && item?.selectedEventCreditQuantity > 0;
     });

     const checkValidCartItems = (!checkOnlyEventNotSelectInCart || checkEventCreditSelected) && !checkEventHasSeatInCart;
     const showSectionTotal = !disableCart && listProductCart.length > 0 && (checkValidCartItems || isInvisiblePurchase);

     const showAddToCart = !isInvisiblePurchase && (!eventHasTrainingCart || checkEventCreditSelected);

     return (
          <div id="individual-products" className="product-type explore-product-card">
               <div className="d-flex title-product-card">
                    <p>
                         {titleTabPersonal?.value}
                         <TooltipWithIcon className="tooltip-info" content={contentTooltipTabPersonal?.value} trigger="mouseenter focus" />
                    </p>
               </div>

               {listProducts.map((pro) => {
                    const trainingProductType = pro.productType?.trainingProductType;
                    const isLiveTraining = trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING;
                    const isPhysical = trainingProductType === TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT;
                    const isBundle = trainingProductType === TRAINING_PRODUCT_TYPES.BUNDLE;

                    const events = pro.events;
                    const isHaveEvent = events && events.length > 0;
                    const isHaveDisabledEvent = !events.some(
                         (e) => e.status !== STATUS.DISABLED && (e.remainingSeats > 0 || e.remainingSeats === null) && !e.isPrivate
                    );

                    const isHasSeat = checkProductHasSeat(listActiveSeatByUser, pro.id) === true && !isLiveTraining && !isPhysical;

                    const isHasReservedSeat = checkProductHasReservedSeat(listActiveSeatByUser, pro.id) === true && !isLiveTraining && !isPhysical;
                    const listBundleProduct = tryParseJSON(pro?.listBundleProduct) || [];

                    //Bundle product only product physical
                    const bundleOnlyPhysical =
                         listBundleProduct.length &&
                         listBundleProduct.every((bundleProduct) => {
                              const productType = products.find((product) => product.id === bundleProduct.id)?.productType.trainingProductType || '';

                              const allowProductType = [TRAINING_PRODUCT_TYPES.PHYSICAL_PRODUCT].includes(productType);
                              return allowProductType;
                         });

                    const isHasSeatBundle =
                         isBundle && listBundleProduct.length
                              ? listBundleProduct.some((bundle) => {
                                     return (
                                          checkProductHasSeat(listActiveSeatByUser, bundle.id) === true &&
                                          checkProductTypeUnlimitedPurchase(products, bundle) &&
                                          !bundleOnlyPhysical
                                     );
                                })
                              : false;

                    const isHasReservedBundle =
                         isBundle && listBundleProduct.length
                              ? listBundleProduct.some((bundle) => {
                                     return (
                                          checkProductHasReservedSeat(listActiveSeatByUser, bundle.id) === true &&
                                          checkProductTypeUnlimitedPurchase(products, bundle) &&
                                          !bundleOnlyPhysical
                                     );
                                })
                              : false;

                    const showTextHasSeat = isHasReservedSeat || isHasSeat || isHasSeatBundle || isHasReservedBundle;

                    const messageHasSeat = isHasReservedSeat
                         ? 'You have an reserved seat, so you cannot purchase it again.'
                         : messageCannotPurchase?.value;

                    //func check awaiting order item
                    const checkHasAwaitingOrderItem = (productCompare) => {
                         return awaitingOrderItems.some((awaitingOrderItem) => {
                              const product = products.find((product) => product.id === awaitingOrderItem.productId);
                              if (!product) {
                                   return false; // No matching product found for the awaitingOrderItem.
                              }
                              const trainingProductType = product?.productType?.trainingProductType;
                              if (trainingProductType === TRAINING_PRODUCT_TYPES.BUNDLE) {
                                   if (!awaitingOrderItems.some((item) => item.productId === productCompare.id)) {
                                        const listBundleProduct = tryParseJSON(product?.listBundleProduct) || [];
                                        return listBundleProduct.some((bundle) => bundle.id === productCompare.id);
                                   }
                              }
                              return awaitingOrderItems.some((item) => item.productId === productCompare.id);
                         });
                    };
                    //handle check awaiting order item conditions
                    const isHasAwaitingOrderItem = checkHasAwaitingOrderItem(pro) && !isLiveTraining && !isPhysical;

                    //handle check awaiting order item list bundle product
                    const isHasAwaitingOrderItemBundle =
                         isBundle && listBundleProduct.length
                              ? listBundleProduct.some((productBundle) => {
                                     return (
                                          checkHasAwaitingOrderItem(productBundle) &&
                                          checkProductTypeUnlimitedPurchase(products, productBundle) &&
                                          !bundleOnlyPhysical
                                     );
                                })
                              : false;

                    const checkNotCustomDate = !pro.customEventEnable || (pro.customEventEnable && pro.customEventMinSeat > 1);

                    const checkEventDisable = checkNotCustomDate && isHaveEvent && isHaveDisabledEvent;

                    const checkHasValidEvent = events.some(
                         (e) =>
                              dayjs(e.startAt).tz(timezoneSelected) >= today &&
                              e.status !== STATUS.DISABLED &&
                              (e.remainingSeats > 0 || e.remainingSeats === null) &&
                              !e.isPrivate
                    );

                    const checkNotEvents =
                         isLiveTraining && checkNotCustomDate && (!isHaveEvent || !checkHasValidEvent) && !pro.allowPurchaseEventCredit;

                    const checkEmptyEvents = checkEventDisable || checkNotEvents;

                    const checkListEvent =
                         pro.productType?.trainingProductType === TRAINING_PRODUCT_TYPES.LIVE_TRAINING &&
                         pro.events.length > 0 &&
                         pro.events.some(
                              (e) =>
                                   dayjs(e.startAt).tz(timezoneSelected) >= today &&
                                   e.status !== STATUS.DISABLED &&
                                   (e.remainingSeats > 0 || e.remainingSeats === null) &&
                                   !e.isPrivate
                         );

                    const checkCeProduct = checkCeProductIsRPT(pro, products);

                    return (
                         <div key={pro.id} className="product-detail pane-training">
                              <div className="card card-product">
                                   <div className="card-body">
                                        <h4>
                                             {pro.genericPublicName}
                                             {(pro?.productType?.trainingProductType === TRAINING_PRODUCT_TYPES.CE_CREDITS ||
                                                  pro?.productType?.trainingProductType === TRAINING_PRODUCT_TYPES.TEST_ONLY) && (
                                                  <TooltipWithIcon
                                                       className="tooltip-info"
                                                       content={contentTooltipCE?.value}
                                                       trigger="mouseenter focus"
                                                  />
                                             )}
                                        </h4>
                                        <div className="card-text">
                                             {pro.shortDescription && (
                                                  <div className="course-description" dangerouslySetInnerHTML={{ __html: pro.shortDescription }} />
                                             )}
                                        </div>

                                        <div className="select-training-product price-training-product">
                                             {showTextHasSeat && !checkCeProduct ? (
                                                  <span className="sub-seat text-danger">{messageHasSeat}</span>
                                             ) : (isHasAwaitingOrderItem || isHasAwaitingOrderItemBundle) && !checkCeProduct ? (
                                                  <span className="sub-seat text-danger">
                                                       You have purchased seat and awaiting payment, so you cannot purchase it again.
                                                  </span>
                                             ) : !PRODUCT_TYPES_REQUIRE_EVENT.includes(pro.productType?.trainingProductType) &&
                                               pro.individualMin > 1 ? (
                                                  <span className="sub-seat text-danger">
                                                       This course must buy at least {pro.individualMin} seats.
                                                  </span>
                                             ) : pro.preventPurchase ? (
                                                  <span className="sub-seat text-secondary">
                                                       {pro.preventPurchaseText || 'This product is not available now'}
                                                  </span>
                                             ) : (
                                                  <>
                                                       {pro?.purchaseType !== CART_TYPES.INVISIBLE ? (
                                                            <>
                                                                 {pro.selected ? (
                                                                      <>
                                                                           {isLiveTraining ? (
                                                                                <div className="select-training-date">
                                                                                     <CButton
                                                                                          className="btn-select-course btn-outline-primary"
                                                                                          type="button"
                                                                                          onClick={() => {
                                                                                               setProductId(pro);
                                                                                          }}
                                                                                     >
                                                                                          SELECT
                                                                                     </CButton>
                                                                                </div>
                                                                           ) : (
                                                                                <CButton
                                                                                     className="btn-border btn btn-select-course btn-primary"
                                                                                     onClick={() => {
                                                                                          setProductId(pro);
                                                                                     }}
                                                                                >
                                                                                     SELECTED
                                                                                </CButton>
                                                                           )}
                                                                      </>
                                                                 ) : (
                                                                      <CButton
                                                                           className="btn-border btn btn-select-course btn-outline-primary"
                                                                           disabled={checkEmptyEvents}
                                                                           onClick={() => {
                                                                                if (!checkEmptyEvents) setProductId(pro);
                                                                           }}
                                                                      >
                                                                           SELECT
                                                                      </CButton>
                                                                 )}
                                                            </>
                                                       ) : (
                                                            <CButton
                                                                 className="btn btn-select-course btn-outline-primary"
                                                                 onClick={() => setProductId(pro)}
                                                            >
                                                                 SELECTED
                                                            </CButton>
                                                       )}
                                                  </>
                                             )}
                                             <span className="price">PRICE</span>
                                             <strong>{`$${pro.price}`}</strong>
                                        </div>
                                        {pro.selected && !checkEmptyEvents && (
                                             <SelectTrainingDate
                                                  productSelected={pro}
                                                  typeOfPurchase={TAB_SELECT_PURCHASE.PERSONAL_TRAINING}
                                                  listProductTypeRequireEvent={listProductTypeRequireEvent}
                                             />
                                        )}
                                        {!checkListEvent && !checkNotCustomDate && !pro.preventPurchase && (
                                             <p className="mt-2 d-block" style={{ fontSize: '15px' }}>
                                                  There are no planned events. You can still purchase a custom event date.
                                             </p>
                                        )}
                                        {checkEmptyEvents && !pro.preventPurchase && (
                                             <p className="mt-2 d-block" style={{ fontSize: '15px' }}>
                                                  There are no planned events, please come back later.
                                             </p>
                                        )}
                                   </div>
                              </div>
                         </div>
                    );
               })}

               {showSectionTotal && (
                    <div className="d-flex sum-total">
                         <div className="total-selection">
                              <h5>TOTAL FOR SELECTION</h5>
                              <strong>
                                   $
                                   {product?.purchaseType === CART_TYPES.INVISIBLE
                                        ? numberWithCommas(product.price?.toFixed(2))
                                        : numberWithCommas(priceSingle?.toFixed(2))}
                              </strong>
                              {product?.purchaseType === CART_TYPES.INVISIBLE && (
                                   <p>
                                        <strong>Product Invisible</strong>
                                   </p>
                              )}
                         </div>

                         {showAddToCart && (
                              <div className="add-to-cart">
                                   <CButton
                                        className="btn btn-blue-highlight btn-block"
                                        disabled={disableCartButton}
                                        onClick={() => {
                                             addToCart(CART_TYPES.INDIVIDUAL);
                                        }}
                                   >
                                        ADD TO CART
                                   </CButton>
                              </div>
                         )}
                    </div>
               )}
          </div>
     );
};

export default PersonalTrainingTab;
